import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { ensureAdsenseLoaderScript, GOOGLE_ADS_CLIENT, GOOGLE_ADS_ENABLED } from '@/lib/google-ads'

type AdFormat = 'auto' | 'vertical' | 'horizontal' | 'rectangle'

declare global {
  interface Window {
    adsbygoogle?: Record<string, unknown>[]
  }
}

export interface GoogleAdSenseProps {
  /** AdSense numeric slot only; omit to render nothing */
  slot?: string
  /** Layout hint forwarded to AdSense — use `vertical` for the narrow sidebar */
  format?: AdFormat
  className?: string
}

/**
 * Responsive display unit. Loads only when `slot` is set (configure in `.env`:
 * see `vite-env.d.ts`). Each instance must have a unique mount in the tree — use
 * different `key`s when repeating the same slot on one page.
 */
export function GoogleAdSense({ slot, format = 'auto', className }: GoogleAdSenseProps) {
  const insRef = useRef<HTMLModElement>(null)
  const didPush = useRef(false)
  const [isBlocked, setIsBlocked] = useState(false)

  useEffect(() => {
    if (!GOOGLE_ADS_ENABLED || !slot) return

    let cancelled = false
    void ensureAdsenseLoaderScript()
      .then(() => {
        if (cancelled || !insRef.current || didPush.current) return
        try {
          didPush.current = true
          ;(window.adsbygoogle = window.adsbygoogle || []).push({})
        } catch {
          didPush.current = false
        }
      })
      .catch(() => {
        didPush.current = false
        setIsBlocked(true)
      })

    return () => {
      cancelled = true
    }
  }, [slot])

  if (!GOOGLE_ADS_ENABLED || !slot || isBlocked) return null

  return (
    <div className={cn('google-ad-scope text-center', className)}>
      <ins
        ref={insRef}
        className="adsbygoogle"
        style={{ display: 'block' }}
        data-ad-client={GOOGLE_ADS_CLIENT}
        data-ad-slot={slot}
        data-ad-format={format}
        {...(format === 'auto' ? { 'data-full-width-responsive': 'true' as const } : {})}
      />
    </div>
  )
}
